import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CircleUserRound, Info } from 'lucide-react';
import { useMapStore } from '../store/mapStore';
import FormSelectTW from './FormSelectTW';

// ----- STATIC OPTIONS (outside component) --------------------
const LEAD_CATEGORY_OPTS = [
  '--None--', 'TOP COMPETITIVE', 'NEW CONSTRUCTION', 'ADDITIONAL LOCATION', 'RECOVER LOST BUSINESS',
  'RETAIL LOCAL', 'RETAIL NATIONAL' //,'Indigenous - Canada' - Not Necessary for USA
];

const LEAD_EXPECTED_TYPE_OPTS = ['--None--', 'TRS', 'TRP', 'LCC', 'CMU', 'Bid & Oth'];

// const LEAD_RATING_OPTS = ['Hot','Warm','Cold','Ready to Buy'];
// const LEAD_BUSINESS_STRUCTURE_OPTS = ['Local','Corporate'];
// const LEAD_CURRENCY_OPTS = ['USD - U.S.Dollar'];
// const LEAD_STATUS_OPTS = ['Open'];

const INDUSTRY_OPTS = [
  '--None--', 'Education', 'Government', 'Healthcare', 'Retail', 'Restaurants & Bars',
  'Travel & Leisure', 'Business & Industry', 'All Other Foodservice'
];

// Sales Call Plan (string picklists)
const SCP_DAY_OPTS = [
  '--None--', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];
const SCP_TIME_OPTS = [
  '--None--', '07:00 AM', '07:30 AM', '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM',
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
  '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM', '06:00 PM'
];


const INDUSTRY_DETAIL_MAP = {
  Education: ['--None--', 'All Other Education', 'College & University', 'K-12', 'Preschool/Daycare'],
  Government: ['--None--', 'All Other Government', 'Corrections', 'Military'],
  Healthcare: ['--None--', 'All Other Healthcare', 'Hospital', 'Long Term Care / Senior Living'],
  Retail: ['--None--', 'All Other Retail', 'C-Stores', 'Grocery/Supermarket'],
  'Restaurants & Bars': ['--None--', 'All Other Restaurants & Bars', 'Bars/Drinking Places/Lounges', 'FSR', 'QSR'],
  'Travel & Leisure': ['--None--',
    'All Other Travel and Leisure', 'Casino x Lodging', 'Lodging',
    'Recreation – All Other', 'Recreation – Country Clubs/Golf Courses', 'Transportation'
  ],
  'Business & Industry': ['--None--', 'Business & Industry'],
  'All Other Foodservice': ['--None--', 'All Other Foodservice'],
};

const PRIMARY_MENU_OPTS = ['--None--',
  'African', 'American', 'Americanized Chinese', 'Authentic Chinese', 'Bakery', 'Bar', 'BBQ', 'Beverages',
  'Brewery', 'Burgers', 'Cafe', 'Cajun/Creole', 'Caribbean', 'Chicken Wings', 'Desserts', 'Diner', 'European',
  'French', 'German', 'Hawaiian', 'Hot Dogs', 'Indian', 'Irish', 'Italian', 'Japanese', 'Korean', 'Med or Mid',
  'Mexican', 'Other', 'Other Asian', 'Pizza', 'Salad', 'Sandwich', 'Seafood', 'Snacks', 'South American',
  'Southern/Soulfood', 'Steak', 'Sushi', 'Tacos', 'Thai', 'Vietnamese'
];

const CUISINE_SEGMENT_MAP = {
  African: ['--None--', 'EMEA'],
  American: ['--None--', 'Mainstream American'],
  'Americanized Chinese': ['--None--', 'Asian'],
  'Authentic Chinese': ['--None--', 'Asian'],
  Bakery: ['--None--', 'Beverages and Snacks'],
  Bar: ['--None--', 'Bar or Brewery'],
  BBQ: ['--None--', 'Single-Focus American'],
  Beverages: ['--None--', 'Beverages and Snacks'],
  Brewery: ['--None--', 'Bar or Brewery'],
  Burgers: ['--None--', 'Single-Focus American'],
  Cafe: ['--None--', 'Beverages and Snacks'],
  'Cajun/Creole': ['--None--', 'Specialty American'],
  Caribbean: ['--None--', 'Latin'],
  'Chicken Wings': ['--None--', 'Single-Focus American'],
  Desserts: ['--None--', 'Beverages and Snacks'],
  Diner: ['--None--', 'Mainstream American'],
  European: ['--None--', 'EMEA'],
  French: ['--None--', 'EMEA'],
  German: ['--None--', 'EMEA'],
  Hawaiian: ['--None--', 'Specialty American'],
  'Hot Dogs': ['--None--', 'Single-Focus American'],
  Indian: ['--None--', 'Asian'],
  Irish: ['--None--', 'EMEA'],
  Italian: ['--None--', 'Italian'],
  Japanese: ['--None--', 'Asian'],
  Korean: ['--None--', 'Asian'],
  'Med or Mid': ['--None--', 'EMEA'],
  Mexican: ['--None--', 'Latin'],
  Other: ['--None--', 'Other'],
  'Other Asian': ['--None--', 'Asian'],
  Pizza: ['--None--', 'Italian'],
  Salad: ['--None--', 'Single-Focus American'],
  Sandwich: ['--None--', 'Sandwich & Deli'],
  Seafood: ['--None--', 'Specialty American'],
  Snacks: ['--None--', 'Beverages and Snacks'],
  'South American': ['--None--', 'Latin'],
  'Southern/Soulfood': ['--None--', 'Specialty American'],
  Steak: ['--None--', 'Mainstream American'],
  Sushi: ['--None--', 'Asian'],
  Tacos: ['--None--', 'Latin'],
  Thai: ['--None--', 'Asian'],
  Vietnamese: ['--None--', 'Asian'],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?1?\d{10}$/; // Matches +1 followed by 10 digits or just 10 digitsconst 
const ZIP_RE = /^\d{5}(?:-\d{4})?$/;                     // 12345 or 12345-6789
const STATE_RE = /^[A-Za-z]{2}$/;                          // US 2-letter code
const CITY_RE = /^[\p{L} .'-]{2,}$/u;                     // letters/spaces, min 2
const SALES_RE = /^-?\d+(?:\.\d+)?$/;                      // integer/decimal


const isBlank = (v) => v === '--None--' || !String(v ?? '').trim();

const isValidCountryUS = (v = '') => {
  const x = v.trim().toLowerCase();
  return x === 'usa' || x === 'us';
};




// ----- COMPONENT --------------------------------------------
const CreateLeadSheet = () => {
  const isMobile = window.innerWidth <= 800;

  // console.log('[lead] rendering CreateLeadSheet window width ', window.innerWidth, 'isMobile? ', isMobile);

  // store
  const isLeadFormOpen = useMapStore(s => s.isLeadFormOpen);
  const leadFormDraft = useMapStore(s => s.leadFormDraft);
  const updateLeadDraft = useMapStore(s => s.updateLeadDraft);
  const submitLead = useMapStore(s => s.submitLead);
  const leadSubmitStatus = useMapStore(s => s.leadSubmitStatus);
  const leadSubmitError = useMapStore(s => s.leadSubmitError);
  const closeLeadForm = useMapStore(s => s.closeLeadForm);
  const selectedLocation = useMapStore(s => s.selectedLocation);
  const userMeta = useMapStore(s => s.userMeta);
  const leadSuccessModal = useMapStore(s => s.leadSuccessModal);


  // local UI state
  const [showMenuInfo, setShowMenuInfo] = useState(false);
  const [showSegInfo, setShowSegInfo] = useState(false);
  const [localError, setLocalError] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');


  // per-field inline errors
  const [fieldErrors, setFieldErrors] = useState({}); // { [fieldName]: string | undefined }

  const isValidUSPhone = (v) => {
    const digits = String(v || '').replace(/\D/g, '');
    return digits.length === 10;
  };
  // Single-field validator (uses the SAME rules you already have)
  const validateField = (name, value, draft) => {
    const v = (value ?? '').toString().trim();

    // requireds
    if (name === 'Company' && (v === '' || v === '--None--')) return 'Company is required.';
    if (name === 'FirstName' && (v === '' || v === '--None--')) return 'First Name is required.';
    if (name === 'LastName' && (v === '' || v === '--None--')) return 'Last Name is required.';
    if (name === 'Category__c' && (v === '' || v === '--None--')) return 'Category is required.';
    if (name === 'Expected_Account_Type__c' && (v === '' || v === '--None--')) return 'Expected Account Type is required.';
    if (name === 'Potential_Avg_Wkly_Sales__c') {
      if (v === '' || v === '--None--') return 'Potential Avg Wkly Sales is required.';
      if (!SALES_RE.test(v)) return 'Potential Avg Wkly Sales must be a number.';
      const num = Number(v);
      if (!Number.isNaN(num) && num < 1250) return 'Potential Avg Wkly Sales must be at least $1,250.';
    }

    // format/content checks
    if (name === 'Email' && v && !EMAIL_RE.test(v)) return 'Enter a valid email address.';
    if (name === 'Phone' && v && !isValidUSPhone(v))
      return 'Enter a valid phone number.';

    if (name === 'MobilePhone' && v && !isValidUSPhone(v))
      return 'Enter a valid phone number.';
    if (name === 'PostalCode' && v && !ZIP_RE.test(v)) return 'Enter a valid ZIP code (e.g., 75063 or 75063-1234).';
    if (name === 'State' && v && !STATE_RE.test(v)) return 'State must be a 2-letter code (e.g., TX).';
    if (name === 'City' && v && !CITY_RE.test(v)) return 'City looks invalid.';
    if (name === 'Country' && v && !isValidCountryUS(v)) return 'Country has to be 2 or 3 characters - US / USA.';

    return undefined;
  };

  // Validate ALL fields (used by trySubmit)
  const validateAll = (draft) => {
    const names = [
      'Company', 'FirstName', 'LastName',
      'Category__c', 'Expected_Account_Type__c', 'Potential_Avg_Wkly_Sales__c',
      'Email', 'MobilePhone', 'Phone', 'PostalCode', 'State', 'City', 'Country' // Use MobilePhone and Phone internally
    ];
    const nextErrors = {};
    names.forEach(n => {
      const err = validateField(n, draft?.[n], draft);
      if (err) nextErrors[n] = err;
    });
    return nextErrors;
  };

  // helper for input class styling
  const inputClass = (name) =>
    `w-full border rounded-lg px-3 py-2 ${fieldErrors[name] ? 'border-red-500 focus:ring-red-500' : ''}`;

  // prevent background scroll when open
  useEffect(() => {
    if (!isLeadFormOpen) return;
    updateLeadDraft({
      Assign_to_DSM__display: userMeta?.dsmName || ''
    });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isLeadFormOpen]);

  // bail (after hooks)
  if (!isLeadFormOpen || !leadFormDraft) return null;

  const d = leadFormDraft;
  const disabled = leadSubmitStatus === 'submitting';
  const onChange = (k) => (eOrValue) => {
    const value = (eOrValue && eOrValue.target !== undefined)
      ? eOrValue.target.value
      : eOrValue; // supports FormSelectTW which sends raw value

    // update the draft
    updateLeadDraft({ [k]: value });

    // validate this field immediately
    const nextDraft = { ...leadFormDraft, [k]: value };
    const err = validateField(k, value, nextDraft);
    setFieldErrors((prev) => ({ ...prev, [k]: err }));
  };

  // dependent values
  const industry = d.Industry || '';
  const industryDetails = industry ? (INDUSTRY_DETAIL_MAP[industry] || []) : [];
  const isRNB = industry === 'Restaurants & Bars';
  const primaryMenu = d.Primary_Menu_Type__c || '';
  const cuisineSegments = isRNB && primaryMenu ? (CUISINE_SEGMENT_MAP[primaryMenu] || []) : [];

  // required validation + guarded submit
  const FIELD_LABELS = {
    Company: 'Company',
    FirstName: 'First Name',
    LastName: 'Last Name',
    Category__c: 'Category',
    Expected_Account_Type__c: 'Expected Account Type',
    Potential_Avg_Wkly_Sales__c: 'Potential Avg Wkly Sales of Oppty',
  };
  const trySubmit = () => {
    const draft = leadFormDraft || {};

    // 1) run the SAME rules on all fields
    const errs = validateAll(draft); // <- this is an OBJECT

    // 2) Sales Call Plan mutual dependency (both or none)
    const scpDay = draft.Sales_Call_Plan_Day__c;
    const scpTime = draft.Sales_Call_Plan_Time__c;

    // Add field-level errors directly to the object (do NOT push)
    if (!isBlank(scpDay) && isBlank(scpTime)) {
      errs.Sales_Call_Plan_Time__c = 'Required if Day is selected';
    }
    if (!isBlank(scpTime) && isBlank(scpDay)) {
      errs.Sales_Call_Plan_Day__c = 'Required if Time is selected';
    }
    
  // Require at least one of Phone/Email/Mobile and show message under Mobile
    if (
      !((draft.Email ?? '').trim() ||
        (draft.Phone ?? '').trim() ||
        (draft.MobilePhone ?? '').trim())
    ) {
      setFieldErrors(prev => ({
        ...prev,
        MobilePhone:
          'Lead should have at least one communication information to create new lead (Phone/Email/Mobile)',
      }));
      setLocalError(''); // prevent bottom summary for this rule
      return;
    }


    // 3) apply & show summary
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      const messages = Object.values(errs); // object -> array of strings
      setLocalError(messages.join(' '));
      return;
    }

    setFieldErrors({});
    setLocalError('');
    submitLead();
  };

  // --- NEW: standard Enter→Next handler (works inside <form>) ---
  const handleEnterNext = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const el = e.currentTarget;
    const form = el.form;
    if (!form) return;

    const fields = Array.from(
      form.querySelectorAll('[data-kb]:not([disabled]):not([aria-disabled="true"])')
    );

    const i = fields.indexOf(el);
    const next = fields[i + 1];

    if (next) {
      next.focus();
      if (typeof next.select === 'function') next.select();
    } else {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else trySubmit();
    }
  };

const formatPhoneNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
 
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
 
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
 
const handlePhoneChange = (e) => {
  const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
  const formatted = formatPhoneNumber(raw);
 
  updateLeadDraft({
    Phone : formatted,
  });
};

 
const handleMobileChange = (e) => {
  const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
  const formatted = formatPhoneNumber(raw);
 
  updateLeadDraft({
    MobilePhone: formatted,
  });
};
 

  const validateForm = () => {
    if (!mobilePhone && !email && !phone) {
      setError('Lead should have at least one communication information to create new lead (Phone/Email/Mobile)');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    // ...existing submission logic...
  };


  /** 1) MOBILE: keep your existing content EXACTLY as-is — just rename to contentMobile */
  const contentMobile = (
    <div className="fixed inset-0 z-[2000]">
      <div className="absolute inset-0 bg-black/40" onClick={disabled ? undefined : closeLeadForm} />
      <div className="absolute inset-0 bg-white flex flex-col">
        {/* Header (blue banner) */}
        <div className="relative">
          <div className="bg-sky-600 text-white  px-4 pb-6 pt-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <CircleUserRound strokeWidth={3} className="h-7 w-7 text-white" fill="none" />
                <div className="text-fluid-xl font-bold">
                  {(leadFormDraft?.Company && leadFormDraft.Company.trim()) ||
                    selectedLocation?.name || 'New Lead'}
                </div>
              </div>
              <button
                className="p-2 rounded-full hover:bg-white/10"
                onClick={disabled ? undefined : closeLeadForm}
                aria-label="Close form"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* helper text bar */}
          <div className="-mt-4 bg-white rounded-t-2xl px-4 py-3 shadow-sm relative z-10">
            <p className="text-sm text-gray-700">
              Please complete the form below to create a new Lead
            </p>
          </div>
        </div>

        {/* Body */}
        {/* NEW: wrap inputs in a real form so Enter→submit works on last field */}
        <form
          onSubmit={(e) => { e.preventDefault(); trySubmit(); }}
          className="flex-1 overflow-y-auto px-4 pb-28"
          noValidate
        >
          {/* Company / Names */}
          <div className="mb-3">
            <label className="block text-sm text-gray-600 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              data-kb
              enterKeyHint="next"
              onKeyDown={handleEnterNext}
              className={inputClass('Company')}
              value={d.Company || ''}
              onChange={onChange('Company')}
              disabled={disabled}
            />
            {fieldErrors.Company && <p className="mt-1 text-xs text-red-600">{fieldErrors.Company}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('FirstName')}
                value={d.FirstName || ''}
                onChange={onChange('FirstName')}
                disabled={disabled}
              />
              {fieldErrors.FirstName && <p className="mt-1 text-xs text-red-600">{fieldErrors.FirstName}</p>}
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('LastName')}
                value={d.LastName || ''}
                onChange={onChange('LastName')}
                disabled={disabled}
              />
              {fieldErrors.LastName && <p className="mt-1 text-xs text-red-600">{fieldErrors.LastName}</p>}
            </div>
          </div>

          {/* Category + Contact + Assign to DSM */}
          <div className="grid grid-cols-1 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <FormSelectTW
                value={d.Category__c || '--None--'}
                onChange={onChange('Category__c')}
                options={LEAD_CATEGORY_OPTS}
                placeholder="--None--"
                disabled={disabled}
                className={fieldErrors.Category__c ? 'ring-1 ring-red-500 border-red-500' : ''}
              />
              {fieldErrors.Category__c && <p className="mt-1 text-xs text-red-600">{fieldErrors.Category__c}</p>}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contact Email</label>
                <input
                  data-kb
                  enterKeyHint="next"
                  onKeyDown={handleEnterNext}
                  className={inputClass('Email')}
                  placeholder="name@example.com"
                  value={d.Email || ''}
                  onChange={onChange('Email')}
                  disabled={disabled}
                />
                {fieldErrors.Email && <p className="mt-1 text-xs text-red-600">{fieldErrors.Email}</p>}
              </div>
              
            <label className="block text-sm text-gray-600 mb-1">Phone</label>
            <input
              data-kb
              enterKeyHint="next"
              onKeyDown={handleEnterNext}
              className={inputClass('Phone')}
              placeholder="(555) 555-5555"
              value={d.Phone || ''}
              onChange={handlePhoneChange}
              disabled={disabled}
            />

            <label className="block text-sm text-gray-600 mb-1">Mobile</label>
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('MobilePhone')} 
                placeholder="(555) 555-5555"
                value={d.MobilePhone || ''}
                onChange={handleMobileChange}
                disabled={disabled}
              />

              {fieldErrors.MobilePhone && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.MobilePhone}</p>
              )}

            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Assign to DSM</label>
              <input
                type="text"
                disabled={true}
                className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                placeholder="DSM Name"
                value={d.Assign_to_DSM__display ?? userMeta?.dsmName ?? ''}
                readOnly
              />
            </div>
          </div>

          {/* Expected Account Type (only) */}
          <div className="mb-3">
            <label className="block text-sm text-gray-600 mb-1">
              Expected Account Type <span className="text-red-500">*</span>
            </label>
            <FormSelectTW
              value={d.Expected_Account_Type__c || '--None--'}
              onChange={onChange('Expected_Account_Type__c')}
              options={LEAD_EXPECTED_TYPE_OPTS}
              placeholder="--None--"
              disabled={disabled}
              className={fieldErrors.Expected_Account_Type__c ? 'ring-1 ring-red-500 border-red-500' : ''}
            />
            {fieldErrors.Expected_Account_Type__c && <p className="mt-1 text-xs text-red-600">{fieldErrors.Expected_Account_Type__c}</p>}
          </div>

          {/* Potential Sales */}
          <div className="mt-3 mb-3">
            <label className="block text-sm text-gray-600 mb-1">
              Potential Avg Wkly Sales of Oppty <span className="text-red-500">*</span>
            </label>
            <input
              data-kb
              enterKeyHint="next"
              onKeyDown={handleEnterNext}
              className={inputClass('Potential_Avg_Wkly_Sales__c')}
              placeholder="e.g. 1500"
              value={d.Potential_Avg_Wkly_Sales__c || ''}
              onChange={onChange('Potential_Avg_Wkly_Sales__c')}
              disabled={disabled}
            />
            {fieldErrors.Potential_Avg_Wkly_Sales__c && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.Potential_Avg_Wkly_Sales__c}</p>
            )}
          </div>

          {/* Default SCP Date & Time */}
          <div className="grid grid-cols-1 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Sales Call Plan Day</label>
              <FormSelectTW
                value={d.Sales_Call_Plan_Day__c || '--None--'}
                onChange={(v) => {
                  updateLeadDraft({ Sales_Call_Plan_Day__c: v });
                  setFieldErrors(prev => {
                    const next = { ...prev, Sales_Call_Plan_Day__c: undefined, Sales_Call_Plan_Time__c: undefined };
                    const day = v;
                    const time = d.Sales_Call_Plan_Time__c;
                    if (!isBlank(day) && isBlank(time)) next.Sales_Call_Plan_Time__c = 'Required if Day is selected';
                    if (!isBlank(time) && isBlank(day)) next.Sales_Call_Plan_Day__c = 'Required if Time is selected';
                    return next;
                  });
                }}
                options={SCP_DAY_OPTS}
                placeholder="--None--"
                disabled={disabled}
              />
              {fieldErrors.Sales_Call_Plan_Day__c && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.Sales_Call_Plan_Day__c}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Sales Call Plan Time</label>
              <FormSelectTW
                value={d.Sales_Call_Plan_Time__c || '--None--'}
                onChange={(v) => {
                  updateLeadDraft({ Sales_Call_Plan_Time__c: v });
                  setFieldErrors(prev => {
                    const next = { ...prev, Sales_Call_Plan_Day__c: undefined, Sales_Call_Plan_Time__c: undefined };
                    const time = v;
                    const day = d.Sales_Call_Plan_Day__c;
                    if (!isBlank(day) && isBlank(time)) next.Sales_Call_Plan_Time__c = 'Required if Day is selected';
                    if (!isBlank(time) && isBlank(day)) next.Sales_Call_Plan_Day__c = 'Required if Time is selected';
                    return next;
                  });
                }}
                options={SCP_TIME_OPTS}
                placeholder="--None--"
                disabled={disabled}
              />
              {fieldErrors.Sales_Call_Plan_Time__c && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.Sales_Call_Plan_Time__c}</p>
              )}
            </div>
          </div>

          {/* Industry + Detail */}
          <div className="grid grid-cols-1 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Industry</label>
              <FormSelectTW
                value={industry}
                onChange={(v) => {
                  updateLeadDraft({
                    Industry: v || '',
                    Industry_Detail__c: '',
                    Primary_Menu_Type__c: '',
                    Cuisine_Segment_Type__c: '',
                  });
                }}
                options={INDUSTRY_OPTS}
                placeholder="--None--"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Industry detail</label>
              <FormSelectTW
                value={d.Industry_Detail__c || ''}
                onChange={(v) => updateLeadDraft({ Industry_Detail__c: v })}
                options={industryDetails}
                placeholder={industry ? '--None--' : 'Select Industry first'}
                disabled={disabled || !industry}
              />
            </div>
          </div>

          {/* Restaurants & Bars specifics */}
          {isRNB && (
            <div className="grid grid-cols-1 gap-3 mb-3">
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  Primary Menu Type
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                    onClick={() => setShowMenuInfo(v => !v)}
                    title="What type of cuisine is your customer most known for?"
                    aria-label="What type of cuisine is your customer most known for?"
                  >
                    <Info className="h-4 w-4 text-gray-400" />
                  </button>
                </label>
                {showMenuInfo && (
                  <p className="text-xs text-gray-500 -mt-2 mb-2">
                    What type of cuisine is your customer most known for?
                  </p>
                )}
                <FormSelectTW
                  value={primaryMenu}
                  onChange={(v) => updateLeadDraft({ Primary_Menu_Type__c: v || '', Cuisine_Segment_Type__c: '' })}
                  options={PRIMARY_MENU_OPTS}
                  placeholder="--None--"
                  disabled={disabled}
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  Cuisine Segment Type
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                    onClick={() => setShowSegInfo(v => !v)}
                    title="What Segment Classification is this customer?"
                    aria-label="What Segment Classification is this customer?"
                  >
                    <Info className="h-4 w-4 text-gray-400" />
                  </button>
                </label>
                {showSegInfo && (
                  <p className="text-xs text-gray-500 -mt-2 mb-2">
                    What Segment Classification is this customer?
                  </p>
                )}
                <FormSelectTW
                  value={d.Cuisine_Segment_Type__c || ''}
                  onChange={(v) => updateLeadDraft({ Cuisine_Segment_Type__c: v })}
                  options={cuisineSegments}
                  placeholder={primaryMenu ? '--None--' : 'Select Primary Menu Type first'}
                  disabled={disabled || !primaryMenu}
                />
              </div>
            </div>
          )}

          {/* Address */}
          <div className="mb-1">
            <label className="block text-sm text-gray-600 mb-1">Address</label>
          </div>
          <div className="grid grid-cols-1 gap-3 mb-3">
            <input
              data-kb
              enterKeyHint="next"
              onKeyDown={handleEnterNext}
              className={inputClass('Street')}
              placeholder="Street"
              value={d.Street || ''}
              onChange={onChange('Street')}
              disabled={disabled}
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('City')}
                placeholder="City"
                value={d.City || ''}
                onChange={onChange('City')}
                disabled={disabled}
              />
              {fieldErrors.City && <p className="mt-1 text-xs text-red-600">{fieldErrors.City}</p>}
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('State')}
                placeholder="State"
                value={d.State || ''}
                onChange={onChange('State')}
                disabled={disabled}
              />
              {fieldErrors.State && <p className="mt-1 text-xs text-red-600">{fieldErrors.State}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                data-kb
                enterKeyHint="next"
                onKeyDown={handleEnterNext}
                className={inputClass('PostalCode')}
                placeholder="Zip/Postal Code"
                value={d.PostalCode || ''}
                onChange={onChange('PostalCode')}
                disabled={disabled}
              />
              {fieldErrors.PostalCode && <p className="mt-1 text-xs text-red-600">{fieldErrors.PostalCode}</p>}
              <input
                data-kb
                enterKeyHint="done"
                onKeyDown={handleEnterNext}
                className={inputClass('Country')}
                placeholder="Country"
                value={d.Country || ''}
                onChange={onChange('Country')}
                disabled={disabled}
              />
              {fieldErrors.Country && <p className="mt-1 text-xs text-red-600">{fieldErrors.Country}</p>}
            </div>
          </div>
        </form>

        {/* Sticky footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
          <div className="flex gap-2">
            <button type="button" className="flex-1 border rounded-lg px-3 py-2" onClick={closeLeadForm} disabled={disabled}>
              Cancel
            </button>
            {leadSubmitStatus !== 'success' && (
              <button
                type="button"
                className="flex-1 bg-blue-600 text-white rounded-lg px-3 py-2 disabled:opacity-60"
                onClick={trySubmit}
                disabled={disabled}
              >
                {leadSubmitStatus === 'submitting' ? 'Submitting…' : (leadSubmitStatus === 'success' ? 'Created!' : 'Create Lead')}
              </button>)}
          </div>
          {(leadSubmitStatus === 'error' || localError) && (
            <div className="mt-2 text-sm text-red-600">
              {localError || leadSubmitError}
            </div>
          )}
        </div>

        {leadSuccessModal && (
          <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/30">
            <div
              role="alert"
              aria-live="polite"
              className="bg-white rounded-xl shadow-xl px-6 py-5 text-center max-w-sm w-[90%] border border-gray-200"
            >
              <div className="text-lg font-semibold text-gray-800">Lead created!</div>
              <p className="mt-1 text-sm text-gray-600">Refreshing the map…</p>
            </div>
          </div>
        )}

      </div>
    </div>

  );

  /** 2) WEB: new centered modal with headings (no blue bar), grouped like your screenshot */
  const contentWeb = (
    <div className="fixed inset-0 z-[2000]">
      {/* Dim background */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={disabled ? undefined : closeLeadForm}
      />

      {/* Centered modal */}
      <div className="absolute inset-0 flex items-start justify-center p-6 overflow-y-auto">
        <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl border border-gray-200">
          {/* Title row (no blue) */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">
              Lead Creation Wizard
            </h2>
            <button
              className="p-2 rounded hover:bg-gray-100"
              onClick={disabled ? undefined : closeLeadForm}
              aria-label="Close form"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Body */}
          <form
            onSubmit={(e) => { e.preventDefault(); trySubmit(); }}
            className="p-6 space-y-8"
            noValidate
          >
            {/* Row 1: Contact (L) + Opportunity (R) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Information */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {/* Company */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Company <span className="text-red-500">*</span>
                    </label>
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('Company')}
                      value={d.Company || ''}
                      onChange={onChange('Company')}
                      disabled={disabled}
                    />
                    {fieldErrors.Company && <p className="mt-1 text-xs text-red-600">{fieldErrors.Company}</p>}
                  </div>

                  {/* First / Last */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        data-kb
                        enterKeyHint="next"
                        onKeyDown={handleEnterNext}
                        className={inputClass('FirstName')}
                        value={d.FirstName || ''}
                        onChange={onChange('FirstName')}
                        disabled={disabled}
                      />
                      {fieldErrors.FirstName && <p className="mt-1 text-xs text-red-600">{fieldErrors.FirstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        data-kb
                        enterKeyHint="next"
                        onKeyDown={handleEnterNext}
                        className={inputClass('LastName')}
                        value={d.LastName || ''}
                        onChange={onChange('LastName')}
                        disabled={disabled}
                      />
                      {fieldErrors.LastName && <p className="mt-1 text-xs text-red-600">{fieldErrors.LastName}</p>}
                    </div>
                  </div>

                  {/* Email / Mobile */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email</label>
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('Email')}
                      placeholder="name@example.com"
                      value={d.Email || ''}
                      onChange={onChange('Email')}
                      disabled={disabled}
                    />
                    {fieldErrors.Email && <p className="mt-1 text-xs text-red-600">{fieldErrors.Email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('Phone')}
                      placeholder="(555) 555-5555"
                      value={d.Phone || ''}
                      onChange={handlePhoneChange}
                      disabled={disabled}
                    />
                    {fieldErrors.Phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.Phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mt-1 mb-1">Mobile</label>
                  <input
                    data-kb
                    enterKeyHint="next"
                    onKeyDown={handleEnterNext}
                    className={inputClass('MobilePhone')}
                    placeholder="(555) 555-5555"
                    value={d.MobilePhone || ''}
                    onChange={handleMobileChange}
                    disabled={disabled}
                  />
                  {fieldErrors.MobilePhone && <p className="mt-1 text-xs text-red-600">{fieldErrors.MobilePhone}</p>}
                </div>
              </section>

              {/* Opportunity Information */}
              <section>
                {/* Assign to DSM (read-only) */}
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Assign to DSM</label>
                  <input
                    type="text" disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed mb-3"
                    placeholder="DSM Name"
                    value={d.Assign_to_DSM__display ?? userMeta?.dsmName ?? ''}
                    readOnly
                  />
                </div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Opportunity Information
                </h3>
                <div className="grid grid-cols-1 gap-3">


                  {/* Category */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <FormSelectTW
                      value={d.Category__c || '--None--'}
                      onChange={onChange('Category__c')}
                      options={LEAD_CATEGORY_OPTS}
                      placeholder="--None--"
                      disabled={disabled}
                      className={fieldErrors.Category__c ? 'ring-1 ring-red-500 border-red-500' : ''}
                    />
                    {fieldErrors.Category__c && <p className="mt-1 text-xs text-red-600">{fieldErrors.Category__c}</p>}
                  </div>

                  {/* Expected Account Type */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Expected Account Type <span className="text-red-500">*</span>
                    </label>
                    <FormSelectTW
                      value={d.Expected_Account_Type__c || '--None--'}
                      onChange={onChange('Expected_Account_Type__c')}
                      options={LEAD_EXPECTED_TYPE_OPTS}
                      placeholder="--None--"
                      disabled={disabled}
                      className={fieldErrors.Expected_Account_Type__c ? 'ring-1 ring-red-500 border-red-500' : ''}
                    />
                    {fieldErrors.Expected_Account_Type__c && <p className="mt-1 text-xs text-red-600">{fieldErrors.Expected_Account_Type__c}</p>}
                  </div>

                  {/* Potential Sales */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Potential Avg Wkly Sales of Oppty <span className="text-red-500">*</span>
                    </label>
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('Potential_Avg_Wkly_Sales__c')}
                      placeholder="e.g. 1500"
                      value={d.Potential_Avg_Wkly_Sales__c || ''}
                      onChange={onChange('Potential_Avg_Wkly_Sales__c')}
                      disabled={disabled}
                    />
                    {fieldErrors.Potential_Avg_Wkly_Sales__c && (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.Potential_Avg_Wkly_Sales__c}</p>
                    )}
                  </div>
                </div>
              </section>
            </div>

            {/* Row 2: Address (L) + Customer Attributes (R) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Address */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Address</h3>
                <div className="grid grid-cols-1 gap-3">
                  <input
                    data-kb
                    enterKeyHint="next"
                    onKeyDown={handleEnterNext}
                    className={inputClass('Street')}
                    placeholder="Street"
                    value={d.Street || ''}
                    onChange={onChange('Street')}
                    disabled={disabled}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('City')}
                      placeholder="City"
                      value={d.City || ''}
                      onChange={onChange('City')}
                      disabled={disabled}
                    />
                    {fieldErrors.City && <p className="mt-1 text-xs text-red-600">{fieldErrors.City}</p>}

                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('State')}
                      placeholder="State"
                      value={d.State || ''}
                      onChange={onChange('State')}
                      disabled={disabled}
                    />
                    {fieldErrors.State && <p className="mt-1 text-xs text-red-600">{fieldErrors.State}</p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      data-kb
                      enterKeyHint="next"
                      onKeyDown={handleEnterNext}
                      className={inputClass('PostalCode')}
                      placeholder="Zip/Postal Code"
                      value={d.PostalCode || ''}
                      onChange={onChange('PostalCode')}
                      disabled={disabled}
                    />
                    {fieldErrors.PostalCode && <p className="mt-1 text-xs text-red-600">{fieldErrors.PostalCode}</p>}

                    <input
                      data-kb
                      enterKeyHint="done"
                      onKeyDown={handleEnterNext}
                      className={inputClass('Country')}
                      placeholder="Country"
                      value={d.Country || ''}
                      onChange={onChange('Country')}
                      disabled={disabled}
                    />
                    {fieldErrors.Country && <p className="mt-1 text-xs text-red-600">{fieldErrors.Country}</p>}
                  </div>
                </div>
              </section>

              {/* Customer Attribute Information */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Customer Attribute Information
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {/* Industry */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Industry</label>
                    <FormSelectTW
                      value={industry}
                      onChange={(v) => {
                        updateLeadDraft({
                          Industry: v || '',
                          Industry_Detail__c: '',
                          Primary_Menu_Type__c: '',
                          Cuisine_Segment_Type__c: '',
                        });
                      }}
                      options={INDUSTRY_OPTS}
                      placeholder="--None--"
                      disabled={disabled}
                    />
                  </div>

                  {/* Industry detail */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Industry Detail</label>
                    <FormSelectTW
                      value={d.Industry_Detail__c || ''}
                      onChange={(v) => updateLeadDraft({ Industry_Detail__c: v })}
                      options={industryDetails}
                      placeholder={industry ? '--None--' : 'Select Industry first'}
                      disabled={disabled || !industry}
                    />
                  </div>

                  {/* Restaurants & Bars specifics */}
                  {isRNB && (
                    <>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Primary Menu Type</label>
                        <FormSelectTW
                          value={primaryMenu}
                          onChange={(v) => updateLeadDraft({ Primary_Menu_Type__c: v || '', Cuisine_Segment_Type__c: '' })}
                          options={PRIMARY_MENU_OPTS}
                          placeholder="--None--"
                          disabled={disabled}
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Cuisine Segment Type</label>
                        <FormSelectTW
                          value={d.Cuisine_Segment_Type__c || ''}
                          onChange={(v) => updateLeadDraft({ Cuisine_Segment_Type__c: v })}
                          options={cuisineSegments}
                          placeholder={primaryMenu ? '--None--' : 'Select Primary Menu Type first'}
                          disabled={disabled || !primaryMenu}
                        />
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* Row 3: Default SCP Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Default SCP Date & Time</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1 py-2">Sales Call Plan Day</label>
                  <FormSelectTW
                    value={d.Sales_Call_Plan_Day__c || '--None--'}
                    onChange={(v) => {
                      updateLeadDraft({ Sales_Call_Plan_Day__c: v });
                      setFieldErrors(prev => {
                        const next = { ...prev, Sales_Call_Plan_Day__c: undefined, Sales_Call_Plan_Time__c: undefined };
                        const day = v;
                        const time = d.Sales_Call_Plan_Time__c;
                        if (!isBlank(day) && isBlank(time)) next.Sales_Call_Plan_Time__c = 'Required if Day is selected';
                        if (!isBlank(time) && isBlank(day)) next.Sales_Call_Plan_Day__c = 'Required if Time is selected';
                        return next;
                      });
                    }}
                    options={SCP_DAY_OPTS}
                    placeholder="--None--"
                    disabled={disabled}
                  />
                  {fieldErrors.Sales_Call_Plan_Day__c && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.Sales_Call_Plan_Day__c}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1 py-2">Sales Call Plan Time</label>
                  <FormSelectTW
                    value={d.Sales_Call_Plan_Time__c || '--None--'}
                    onChange={(v) => {
                      updateLeadDraft({ Sales_Call_Plan_Time__c: v });
                      setFieldErrors(prev => {
                        const next = { ...prev, Sales_Call_Plan_Day__c: undefined, Sales_Call_Plan_Time__c: undefined };
                        const time = v;
                        const day = d.Sales_Call_Plan_Day__c;
                        if (!isBlank(day) && isBlank(time)) next.Sales_Call_Plan_Time__c = 'Required if Day is selected';
                        if (!isBlank(time) && isBlank(day)) next.Sales_Call_Plan_Day__c = 'Required if Time is selected';
                        return next;
                      });
                    }}
                    options={SCP_TIME_OPTS}
                    placeholder="--None--"
                    disabled={disabled}
                  />
                  {fieldErrors.Sales_Call_Plan_Time__c && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.Sales_Call_Plan_Time__c}</p>
                  )}
                </div>
              </section>
            </div>


            {/* Form footer */}
            <div className="pt-4 border-t border-gray-200 flex gap-2">
              <button
                type="button"
                className="flex-1 border rounded-lg px-3 py-2"
                onClick={closeLeadForm}
                disabled={disabled}
              >
                Cancel
              </button>
              {leadSubmitStatus !== 'success' && (
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white rounded-lg px-3 py-2 disabled:opacity-60"
                  disabled={disabled}
                >
                  {leadSubmitStatus === 'submitting' ? 'Submitting…' : (leadSubmitStatus === 'success' ? 'Created!' : 'Create Lead')}
                </button>
              )}
            </div>

            {(leadSubmitStatus === 'error' || localError) && (
              <div className="text-sm text-red-600">{localError || leadSubmitError}</div>
            )}
          </form>

          {/* Success modal (kept) */}
          {leadSuccessModal && (
            <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/30">
              <div role="alert" aria-live="polite" className="bg-white rounded-xl shadow-xl px-6 py-5 text-center max-w-sm w-[90%] border border-gray-200">
                <div className="text-lg font-semibold text-gray-800">Lead created!</div>
                <p className="mt-1 text-sm text-gray-600">Refreshing the map…</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );


  return createPortal(isMobile ? contentMobile : contentWeb, document.body);
}

export default CreateLeadSheet;
